import { publicHref } from '../lib/publicUrls';
import { useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Building2, CalendarDays, MapPin, Users } from 'lucide-react';
import SEO from '../components/SEO';
import HeroVideo from '../components/HeroVideo';
import MediaTruthSurface from '../components/MediaTruthSurface';
import TourInvitation from '../components/TourInvitation';
import { TourClosing } from '../components/TourJourney';
import useReveal from '../hooks/useReveal';
import { MEDIA_SLOTS, mediaDataProps } from '../lib/mediaTruth';
import { emitSemanticEvent } from '../lib/semanticEvents';

// Every program and class section, MX4 first (owner direction, Sept 30). Large cards span two
// columns: MX4 at the top and All Programs at the end.
const experiences = [
  {
    title: 'MX4',
    description: 'Cardio, strength, endurance',
    media: MEDIA_SLOTS.homeMx4,
    link: '/programs/mx4',
    large: true,
  },
  {
    title: 'Personal Training',
    description: 'Individual guidance',
    media: MEDIA_SLOTS.homePersonalTraining,
    link: '/programs/personal-training',
  },
  {
    title: 'Reformer Pilates',
    description: 'Strength with control',
    media: MEDIA_SLOTS.homeReformerPilates,
    link: '/programs/reformer-pilates',
  },
  {
    title: 'Aquatics',
    description: 'Lessons, laps, and movement',
    media: MEDIA_SLOTS.homeAquatics,
    link: '/programs/swim',
  },
  {
    title: 'Kids & Family',
    description: 'Room for every generation',
    media: MEDIA_SLOTS.homeKidsFamily,
    link: '/programs/kids-and-family',
  },
  {
    title: 'Cardio',
    description: 'Spin, Zumba, and kickboxing',
    media: MEDIA_SLOTS.homeCardio,
    link: '/schedule/cardio',
  },
  {
    title: 'Mind-Body',
    description: 'Yoga, barre, and stretch',
    media: MEDIA_SLOTS.homeMindBody,
    link: '/schedule/mind-body',
  },
  {
    title: 'Weight Training',
    description: 'Group strength classes',
    media: MEDIA_SLOTS.homeWeightTraining,
    link: '/schedule/weight-training',
  },
  {
    title: 'Recovery',
    description: 'Massage, sauna, and reset',
    media: MEDIA_SLOTS.homeRecovery,
    link: '/programs/massage',
  },
  {
    title: 'All Programs',
    description: 'A program for every experience level and goal',
    media: MEDIA_SLOTS.homeAllPrograms,
    link: '/programs',
    large: true,
  },
];

// Each fact already appears in approved site copy (About page and homepage).
const reasons = [
  { icon: CalendarDays, title: 'Since 2002', detail: 'Family owned and locally connected.' },
  { icon: MapPin, title: 'Two clubs', detail: 'Brentwood and Discovery Bay.' },
  { icon: Building2, title: '55,000+ square feet', detail: 'In Brentwood, with 12+ dedicated rooms for exercise and wellness.' },
  { icon: Users, title: 'For every age', detail: 'Programs for children through adults.' },
];

const locations = [
  {
    key: 'brentwood',
    index: '01',
    label: 'Primary club',
    name: 'Brentwood',
    address: <>120 Guthrie Ln.<br />Brentwood, CA 94513</>,
    hours: <>Mon–Thu 4:30am–10:30pm<br />Fri 4:30am–9:00pm<br />Sat–Sun 6:00am–6:00pm</>,
    map: 'https://maps.google.com/?q=120+Guthrie+Ln+Brentwood+CA+94513',
  },
  {
    key: 'discovery_bay',
    index: '02',
    label: 'East County',
    name: 'Discovery Bay',
    address: <>1520 Discovery Bay Blvd.<br />Discovery Bay, CA 94505</>,
    hours: <>Mon–Thu 6:00am–8:00pm<br />Fri 6:00am–5:00pm<br />Sat–Sun 7:00am–2:00pm</>,
    map: 'https://maps.google.com/?q=1520+Discovery+Bay+Blvd+Discovery+Bay+CA+94505',
  },
];

const HomePage = () => {
  const location = useLocation();
  const homeRef = useRef(null);
  const openingRef = useRef(null);
  useReveal(homeRef);


  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'HealthClub',
    name: 'Delta Valley Health Club',
    description: 'Hometown health club serving Brentwood and Discovery Bay, California since 2002.',
    url: 'https://deltavalleyhealthclub.com',
    telephone: '(925) 240-2990',
    email: 'info@deltavac.com',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '120 Guthrie Ln',
      addressLocality: 'Brentwood',
      addressRegion: 'CA',
      postalCode: '94513',
      addressCountry: 'US',
    },
    openingHoursSpecification: [
      { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday'], opens: '04:30', closes: '22:30' },
      { '@type': 'OpeningHoursSpecification', dayOfWeek: 'Friday', opens: '04:30', closes: '21:00' },
      { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday', 'Sunday'], opens: '06:00', closes: '18:00' },
    ],
  };

  const recordTourIntent = (placement) => {
    emitSemanticEvent('book_tour_cta_click', {
      page_path: location.pathname,
      cta_placement: placement,
      component_context: 'homepage',
      destination: '/book-a-tour',
      location_context: 'unspecified',
    });
  };

  const recordLocation = (destination, locationContext) => {
    emitSemanticEvent('location_interaction', {
      page_path: location.pathname,
      component_context: 'homepage_location_pair',
      destination,
      location_context: locationContext,
    });
  };

  return (
    <main className="dv-home" ref={homeRef}>
      <SEO
        title="Delta Valley Health Club | Brentwood, CA"
        description="Delta Valley Health Club is Brentwood's hometown health club, offering fitness, personal training, swimming, Reformer Pilates, MX4, childcare, and more."
        keywords="gym Brentwood CA, Delta Valley Health Club, personal training Brentwood, swim lessons Brentwood, Reformer Pilates Brentwood"
        schema={organizationSchema}
      />

      <section className="dv-home-opening" aria-labelledby="home-title" ref={openingRef}>
        <HeroVideo
          {...mediaDataProps(MEDIA_SLOTS.homeHeroMotion)}
          src={MEDIA_SLOTS.homeHeroMotion.src}
          poster={MEDIA_SLOTS.homeHeroPoster.src}
          className="dv-home-opening__media"
        />
        <div className="dv-home-opening__veil" aria-hidden="true" />
        <div className="dv-home-opening__frame">
          <p className="dv-eyebrow">Brentwood · Discovery Bay · Est. 2002</p>
          <h1 className="dv-size-heading" id="home-title" aria-label="Commit to Fit">
            <span>Commit</span>
            <em>to fit</em>
          </h1>
          <p className="dv-home-opening__strap">Fitness · Wellness · Community</p>
          <div className="dv-home-opening__actions">
            <Link
              className="dv-button dv-button--light"
              to={publicHref("/book-a-tour")}
              onClick={() => recordTourIntent('hero')}
            >
              Book a Tour
            </Link>
            <Link className="dv-button dv-home-button--ghost" to={publicHref("/membership")}>
              View Memberships
            </Link>
          </div>
        </div>
      </section>

      <section className="dv-home-welcome" id="home-introduction" aria-labelledby="home-introduction-title">
        <div data-reveal>
          <p className="dv-eyebrow">Your hometown health club</p>
          <h2 className="dv-size-heading" id="home-introduction-title">Stronger lives.<br /><em>Closer community.</em></h2>
          <p className="dv-size-lede">Since 2002, Delta Valley has brought together fitness, wellness, and community.</p>
          <p className="dv-size-body">Two East County clubs. Programs for different ages, experience levels, and goals. A team rooted in the community it serves.</p>
        </div>
      </section>

      <section className="dv-home-programs" aria-labelledby="experience-title">
        <div className="dv-home-section-head" data-reveal>
          <p className="dv-eyebrow">Find your way in</p>
          <h2 className="dv-size-heading" id="experience-title">One club.<br /><em>Many ways to move.</em></h2>
        </div>
        <ul className="dv-home-programs__grid">
          {experiences.map((experience, index) => (
            <li key={experience.title} className={experience.large ? 'dv-home-programs__item--large' : undefined} data-reveal style={{ '--dv-reveal-order': index % 3 }}>
              <Link className="dv-home-card" to={publicHref(experience.link)}>
                <MediaTruthSurface media={experience.media} className="dv-home-card__media" />
                <span className="dv-home-card__body">
                  <h3 className="dv-size-subheading">{experience.title}</h3>
                  <span className="dv-size-body">{experience.description}</span>
                  <span className="dv-home-card__cue" aria-hidden="true">Explore</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <section className="dv-home-why" aria-labelledby="why-title">
        <div className="dv-home-why__intro" data-reveal>
          <p className="dv-eyebrow">Why Delta Valley</p>
          <h2 className="dv-size-heading" id="why-title">A club should feel like it belongs to the people inside it.</h2>
          <p className="dv-size-body">Come for the equipment, classes, pools, and coaching. Stay for the familiar faces and the feeling that your goals have a place here.</p>
          <Link className="dv-text-link" to={publicHref("/membership")}>
            Explore memberships <span aria-hidden="true">↗</span>
          </Link>
        </div>
        <ul className="dv-home-why__facts">
          {reasons.map(({ icon: Icon, title, detail }, index) => (
            <li key={title} data-reveal style={{ '--dv-reveal-order': index }}>
              <Icon aria-hidden="true" />
              <h3 className="dv-size-subheading">{title}</h3>
              <p className="dv-size-body">{detail}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="dv-home-visit" id="locations" aria-labelledby="locations-title">
        <div className="dv-home-section-head" data-reveal>
          <p className="dv-eyebrow">Two clubs · One community</p>
          <h2 className="dv-size-heading" id="locations-title">Find your<br /><em>Delta Valley.</em></h2>
        </div>
        <div className="dv-home-visit__clubs">
          {locations.map((club) => (
            <article
              data-reveal
              key={club.key}
              className={`dv-home-club ${club.key === 'brentwood' ? 'dv-home-club--primary' : ''}`}
              aria-labelledby={`home-club-${club.key}`}
            >
              <p className="dv-home-club__index">{club.index} · {club.label}</p>
              <h3 className="dv-size-subheading" id={`home-club-${club.key}`}>{club.name}</h3>
              <address>{club.address}</address>
              <p className="dv-home-club__hours">{club.hours}</p>
              <div className="dv-home-club__links">
                <a
                  href="tel:+19252402990"
                  onClick={() => emitSemanticEvent('phone_click', {
                    page_path: location.pathname,
                    component_context: 'homepage_location_pair',
                    destination: 'tel:+19252402990',
                    location_context: club.key,
                  })}
                >
                  (925) 240-2990
                </a>
                <a
                  href={publicHref(club.map)}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => recordLocation('google_maps', club.key)}
                >
                  Directions
                </a>
              </div>
            </article>
          ))}
        </div>
      </section>

      <TourClosing />

      <TourInvitation triggerRef={openingRef} />
    </main>
  );
};

export default HomePage;
